import { ListData_ValueTypes, QuickEntry, quickEntryChange, SelectFieldOption_Values, TableStyles_Types } from "../../../Definitions";
import { useAppSelector } from "../../../Store";
import { useQuery } from "@apollo/client";
import { FilteredAnimalsResponse, GET_FILTERED_ANIMALS } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getFilteredAnimalsCall";
import { MutableRefObject, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Graph_QL } from "../../../Services";
import { GenericDataParameters, GenericRow } from "../../Tables/_Tables/useColumnConstructor";
import { useEditableColumnConstructor } from "../../Tables/_Tables/useEditableColumnConstructor";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, noop, Row, useReactTable } from "@tanstack/react-table";
import { ReactiveTable } from "../../Tables/reactiveTableRenderer";
import { useSelectOptions } from "../../_Components";
import { PanelMaintainAnimal } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/maintainAnimalInfoCall";
import { keyBy, merge } from "lodash";
import { useFeedCurves } from "../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves";
import { colors, fonts } from "../../../Styles";

type QuickEntryModalLvl3Props = {
  quickEntryData: QuickEntry[];
  quickEntryChange: quickEntryChange[];
  dirtyDataRef: MutableRefObject<PanelMaintainAnimal[]>
};
interface TableRow extends GenericRow {
  index: number;
  animalNo: number;
  animalGroup?: { label: string; id: number };
  animalLatestMilestone?: { label: string; id: number };
  animalFeedcurve?: SelectFieldOption_Values;
};

const camelToSnakeCase = (stringValue: string) => {
  // the table id for feed curve is with misspelled feedcurve in the database
  return stringValue === "feedCurveId" ? "feedcurve_id": stringValue.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
};

export default function QuickEntryModalLvl3({ quickEntryData, quickEntryChange, dirtyDataRef }: QuickEntryModalLvl3Props) {
  const { t } = useTranslation();
  const [editTable] = useState(true)
  const { feedcurveOptions, groupOptions, milestoneOptions } = useSelectOptions();
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  const { data: feedCurvesState } = useFeedCurves(connection); 

  const feedCurves = useMemo(() => {
    if (feedCurvesState && feedCurvesState[domain]) {
      return feedCurvesState[domain].data;
    }
    return [];
  }, [domain, feedCurvesState]);
  const filters = quickEntryData.map((filter) => ({
    ids: filter.value.map(({ id }) => id),
    key: camelToSnakeCase(filter.key),
  }));

  const { loading, data } = useQuery<FilteredAnimalsResponse>(
    GET_FILTERED_ANIMALS,
    { variables: { domain: farm, filters } }
  );
  const tableData = useMemo(() => {
    if (!data?.animal.filtered) return [];
  
    return data.animal.filtered
    .map((filteredAnimal, index): TableRow | null => {

      const animalGroupChange = quickEntryChange.find(q => q.key === "animalGroupId");
      const milestoneChange = quickEntryChange.find(q => q.key === "milestone_id");
      const feedCurveChange = quickEntryChange.find(q => q.key === "feedCurveId");
      const animalFeedCurve = feedCurves.find(({feedCurveId}) => (
        feedCurveId === feedCurveChange?.value[0].id 
      )) ?? feedCurves.find(({feedCurveId}) => (feedCurveId === filteredAnimal.feedcurve_id));
      const animalMilestone = animalFeedCurve?.milestones?.find(({milestoneId}) => (milestoneId === milestoneChange?.value[0].id)) ?? animalFeedCurve?.milestones?.find(({milestoneId}) => (milestoneId === filteredAnimal.milestone_id));
        
      const changedData: PanelMaintainAnimal = {animalId: filteredAnimal.animal_id};
      if (animalGroupChange) {
        changedData.animal_group_id = animalGroupChange.value[0].id          
      }
      if (milestoneChange && animalMilestone && (animalMilestone.milestoneId !== filteredAnimal.milestone_id || filteredAnimal.feedcurve_id !== animalFeedCurve?.feedCurveId)) {
        changedData.fk_feedcurve_milestones = animalMilestone.feedCurveMilestoneId;
      }
      if (feedCurveChange) {
        changedData.feedCurveId = feedCurveChange.value[0].id
      }
      dirtyDataRef.current = Object.values(merge({}, keyBy(dirtyDataRef.current, "animalId"), keyBy([changedData], "animalId")
      ));
      
      const _groupOptions = groupOptions.filter(option => option.id !== undefined) as { label: string; id: number }[]
      
      return {
        id: filteredAnimal.animal_id,
        animalNo: filteredAnimal.animal_number,
        animalGroup: _groupOptions.find(({ id }) => id === animalGroupChange?.value[0].id),
        animalLatestMilestone: animalMilestone ? {id: animalMilestone.milestoneId, label: animalMilestone.name}: undefined, 
        animalFeedcurve: animalFeedCurve ? {id: animalFeedCurve.feedCurveId, label: animalFeedCurve.name} : undefined,
        index,
      };
    })
    .filter((row): row is TableRow => row !== null);
  }, [data?.animal.filtered, quickEntryChange, feedCurves, groupOptions, dirtyDataRef]);
  
  const accessorData = useMemo<GenericDataParameters<TableRow>[]>(() => {
    const rows: GenericDataParameters<TableRow>[] = [
      { headerValue: `${t("Animal No.", { defaultValue: "Animal No." })}`, accessor: "animalNo", type: ListData_ValueTypes.String }
    ];
  
    if (quickEntryChange.some(f => f.key === "animalGroupId")) {
      rows.push({ headerValue: t("params.group", { defaultValue: "group" }), accessor: "animalGroup", type: ListData_ValueTypes.List, options: groupOptions });
    }
    if (quickEntryChange.some(f => f.key === "milestone_id")) {
      rows.push({ headerValue: t("params.milestone", { defaultValue: "milestone" }), accessor: "animalLatestMilestone", type: ListData_ValueTypes.List, options: (row?: Row<TableRow>) => {
          if (!row) {
            return milestoneOptions;
          }
          return (
            feedCurves
              .find(
                (feedCurve) =>
                  row.original.animalFeedcurve?.id === feedCurve.feedCurveId
              )
              ?.milestones?.map((milestone) => ({
                id: milestone.feedCurveMilestoneId,
                label: milestone.name,
              })) ?? []
          );
        }, });
    }

    if (quickEntryChange.some(f => f.key === "feedCurveId")) {
      rows.push({ headerValue: t("params.feedCurve", { defaultValue: "feed curve" }), accessor: "animalFeedcurve", type: ListData_ValueTypes.List, options: feedcurveOptions });
    }
  
    return rows;
  }, [t, quickEntryChange, groupOptions, feedCurves, milestoneOptions, feedcurveOptions]);

  const columns = useEditableColumnConstructor({
    accessorData: [...accessorData], 
    tableStyle: TableStyles_Types.blueTable, 
    canEdit: editTable 
  });

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      columnPinning: {
        left: ["animalNo"]
      }
    },
    meta: {
      deleteRow: noop,
      updateData: (rowId, columnId, value, row) => {
        if (columnId === "animalGroup") {
          const selectValue = value as any as SelectFieldOption_Values;
          dirtyDataRef.current = Object.values(
            merge({}, keyBy(dirtyDataRef.current, "animalId"), keyBy([{
              animalId: rowId,
              animal_group_id: selectValue.id
            }], "animalId")
            )
          );
          return;
        }
        if (columnId === "animalFeedcurve") {
          const selectValue = value as any as SelectFieldOption_Values;

          const selectedFeedCurve = feedCurves.find(
            (feedCurve) => feedCurve.feedCurveId === Number(selectValue.id)
          );

          dirtyDataRef.current = Object.values(
            merge({}, keyBy(dirtyDataRef.current, "animalId"), keyBy([{
              animalId: rowId,
              feedCurveId: selectValue.id,
              fk_feedcurve_milestones: selectedFeedCurve?.milestones
                ?.length
                ? selectedFeedCurve?.milestones[0].feedCurveMilestoneId
                : undefined,
            }],"animalId"))
          );
          return;
        }
        if (columnId === "animalLatestMilestone") {
          const selectValue = value as any as SelectFieldOption_Values;
          if (row.original.animalFeedcurve?.id) {
            const animalFeedCurve = feedCurves.find(
              (feedCurve) =>
                feedCurve.feedCurveId === row.original.animalFeedcurve?.id
            );
            const milestone = animalFeedCurve?.milestones?.find(
              (milestone) =>
                milestone.feedCurveMilestoneId === Number(selectValue.id)
            );
            if (milestone) {
              dirtyDataRef.current = Object.values(
                merge({}, keyBy(dirtyDataRef.current, "animalId"), keyBy([{
                  animalId: rowId,
                  fk_feedcurve_milestones: milestone.feedCurveMilestoneId
                }], "animalId" ))
              );
            }
          }
          return;
        }
      }
    }
  })
  const reactiveTable = useMemo(() => {if (tableData.length!==0) {return ReactiveTable({table, tableType: TableStyles_Types.blueTable, emptyText: t("loginFlow.login.noSitesFound", {defaultValue: "no sites found"}), renderFooter: false})}}, [t, table, tableData.length])
  
  if (loading) {
    return ( <div style={{width: "100%", display: "flex", justifyContent: "center"}}>
      <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary}}>Loading…</p>
    </div> );
  }
  
  return ( <div style={{paddingTop: "20px"}}>
    {reactiveTable}
  </div> );
}